// Lightweight notification sound using the Web Audio API (no audio asset required).

let audioCtx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioCtx) {
      const Ctor: typeof AudioContext =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      audioCtx = new Ctor();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

/**
 * Play a short two-tone chime. `variant` shapes the tone for the event severity:
 * - 'success' -> rising pleasant chime
 * - 'error'   -> lower, doubled buzz
 * - 'info'    -> single soft tone
 */
export function playNotificationSound(variant: 'success' | 'error' | 'info' = 'info'): void {
  const ctx = getCtx();
  if (!ctx) return;
  // Browsers suspend the context until a user gesture; resume best-effort.
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const now = ctx.currentTime;
  const tones: Array<{ freq: number; start: number; dur: number }> =
    variant === 'success'
      ? [
          { freq: 660, start: 0, dur: 0.12 },
          { freq: 990, start: 0.12, dur: 0.16 },
        ]
      : variant === 'error'
        ? [
            { freq: 320, start: 0, dur: 0.16 },
            { freq: 280, start: 0.18, dur: 0.22 },
          ]
        : [{ freq: 740, start: 0, dur: 0.18 }];

  for (const tone of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = tone.freq;
    const t0 = now + tone.start;
    const t1 = t0 + tone.dur;
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t1);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t1 + 0.02);
  }
}

/** Prime the audio context on a user gesture so later programmatic sounds are allowed. */
export function primeNotificationSound(): void {
  const ctx = getCtx();
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
}
